import { produce } from 'immer';

const initialState = {
  currentInteraction: {
    type: 'questStart',
    response: '',
    id: 'initial',
  },
  interactionWindowVisible: false,
  responses: {},
  interactionThread: [],
  alternativeResponse: '',
  status: 'wait_user',
  server_action: '',
};

const interactionReducer = (state = initialState, action) => {
  return produce(state, (draft) => {
    switch (action.type) {
      case 'INTERACTION_WINDOW_OPEN':
        // Action pour ouvrir la fenêtre d'interaction sans changer l'interaction
        draft.interactionWindowVisible = true;
        break;

      case 'INTERACTION_WINDOW_CLOSE':
        // Action pour fermer la fenêtre d'interaction sans changer l'interaction
        draft.interactionWindowVisible = false;
        break;
      case 'INTERACTION_PROGRESS_BY_AI':
        draft.server_action = action.payload;
        break;

      case 'INTERACTION_INFO': {
        const infoInteraction = {
          response: action.payload,
          id: `interaction-${Date.now()}`,
          timestamp: new Date().toISOString(),
          // Marquer cette interaction comme venant du serveur
          source: 'server',
        };
        draft.interactionThread.push(infoInteraction);
        draft.currentInteraction = {
          type: 'questStart',
          response: '',
          id: 'initial',
        };
        break;
      }

      case 'INTERACTION_CLOSED_BY_AI': {
        if (state.currentInteraction.id == 'initial') break;

        const infoInteraction = {
          response: action.payload,
          id: `interaction-${Date.now()}`,
          timestamp: new Date().toISOString(),
          // Marquer cette interaction comme venant du serveur
          source: 'server',
        };
        draft.interactionThread.push(infoInteraction);
        draft.currentInteraction = {
          type: 'questStart',
          response: '',
          id: 'initial',
        };
        break;
      }

      case 'INTERACTION_RESET': {
        return initialState;
        break;
      }

      case 'INTERACTION_START_NEW': {
        draft.currentInteraction = {
          type: action.payload.type,
          data: action.payload.data,
          response: '',
        };
        draft.interactionWindowVisible = true;
        draft.interactionThread = [];
        draft.status = 'wait_user';
        break;
      }

      case 'INTERACTION_OPEN': {
        // Action envoyée par le serveur pour démarrer une nouvelle interaction
        draft.currentInteraction = {
          type: action.payload.interactionType,
          data: action.payload.interactionData,
          response: '',
          id: `interaction-${Date.now()}`,
          timestamp: new Date().toISOString(),
          question: action.payload.interactionData?.question || 'Interaction',
          // Marquer cette interaction comme venant du serveur
          source: 'server',
        };

        // Rendre la fenêtre visible car le serveur a demandé une interaction
        draft.interactionWindowVisible = true;
        draft.status = 'wait_user';
        draft.server_action = '';
        break;
      }

      case 'INTERACTION_CLOSE':
        // Action envoyée par le client pour fermer l'interaction
        // Nous ne supprimons pas l'interaction, juste fermer la fenêtre
        draft.interactionWindowVisible = false;
        break;

      case 'INTERACTION_RESPONSE': {
        // Action de réponse du client vers le serveur
        const { questionId, response, isAlternative, finish } = action.payload;

        if (finish) {
          return initialState;
          break;
        }

        // For alternative responses that shouldn't close the current interaction
        if (isAlternative === true) {
          // Create a new thread item for the alternative response
          const alternativeInteraction = {
            type: 'alternativeResponse',
            id: `alternative-${Date.now()}`,
            question: response,
            response: draft.currentInteraction.data?.question || 'Interaction',
            timestamp: new Date().toISOString(),
            source: 'client',
            isAlternative: true,
          };

          // Add to thread but don't close current interaction
          draft.interactionThread.push(alternativeInteraction);

          // Clear the alternative response field
          draft.alternativeResponse = '';

          // Don't change the interaction state for alternative responses
          break;
        }

        // Standard response flow (non-alternative)
        // Créer une entrée dans l'historique basée sur l'interaction et la réponse
        const completedInteraction = {
          type: draft.currentInteraction.type,
          data: draft.currentInteraction.data,
          id: draft.currentInteraction.id,
          question: action.payload.response,
          response: draft.currentInteraction.data?.question || 'Interaction',
          completedAt: new Date().toISOString(),
          // Conserver le marqueur de source
          source: draft.currentInteraction.source,
        };
        draft.status = 'wait_server';
        draft.server_action = '';

        // Ajouter au thread
        draft.interactionThread.push(completedInteraction);

        // Stocker la réponse dans le tableau des réponses si un questionId est fourni
        if (questionId) {
          draft.responses[questionId] = response;
        }

        // Réinitialiser l'interaction courante
        draft.currentInteraction.data = {};

        // Clear the alternative response as well
        draft.alternativeResponse = '';

        // Garder la fenêtre visible - le serveur décidera de la suite
        draft.interactionWindowVisible = true;
        break;
      }

      case 'VOICE_TO_TEXT_INPUT_DONE':
        // Mettre à jour le texte de réponse après reconnaissance vocale
        if (action.targetField === 'alternative') {
          // Update the alternative response field if specified
          draft.alternativeResponse = action.objects.transcription;
        } else {
          // Otherwise update the current interaction response
          draft.currentInteraction.response = action.objects.transcription;
        }
        break;

      case 'VOICE_TO_TEXT_INPUT_CLIENT_CHANGED':
        // Handle updates to alternative response or standard response
        if (action.targetField === 'alternative') {
          // Update alternative response when that field is being edited
          if (draft.alternativeResponse !== action.transcription) {
            draft.alternativeResponse = action.transcription;
          }
        } else if (draft.currentInteraction.response !== action.transcription) {
          draft.currentInteraction.response = action.transcription;
        }

        break;

      case 'AI_ASK_SUCCESS': {
        // Réponse du serveur après une demande AI
        // Créer une entrée dans l'historique avec la réponse AI
        const aiInteraction = {
          type: 'aiResponse',
          question: draft.currentInteraction.response,
          aiResponse: action.payload.response,
          timestamp: new Date().toISOString(),
          id: `ai-response-${Date.now()}`,
          source: 'client',
        };

        // Ajouter au début du thread
        draft.interactionThread.unshift(aiInteraction);

        // Réinitialiser l'interaction courante
        draft.currentInteraction = {
          type: 'questStart',
          response: '',
          id: `interaction-${Date.now()}`,
        };
        draft.alternativeResponse = '';
        break;
      }
      default:
        break;
    }
  });
};

export default interactionReducer;
